import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { logError } from "@/lib/logger";

/**
 * Reusable autocomplete <input> for free-text fields where consistency
 * matters (locations, salary bands, industries, etc.).
 *
 * Suggestion sources are merged, de-duplicated, and ranked by fuzzy match:
 *   • `staticOptions` — passed in from callers (curated catalogs)
 *   • `field` — optional; triggers GET /api/suggestions/{field} on mount so
 *     the workspace's own historical values also surface
 *   • `extraOptions` — optional extra props for last-mile injection
 *
 * Keyboard: ArrowDown / ArrowUp navigate, Enter selects, Esc closes.
 * All render surfaces (dropdown item, clear button) carry data-testids.
 *
 * Props:
 *   value, onChange, placeholder, staticOptions, field, testId, required,
 *   className (root input class), extraOptions, disabled, maxOptions
 */
export default function AutocompleteInput({
    value,
    onChange,
    placeholder = "",
    staticOptions = [],
    field,
    extraOptions = [],
    testId,
    required = false,
    disabled = false,
    maxOptions = 8,
    className = "",
    onEnter,
}) {
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    const [remote, setRemote] = useState([]);
    const rootRef = useRef(null);
    const listRef = useRef(null);

    // Load DB-backed suggestions once per field
    useEffect(() => {
        if (!field) return;
        let mounted = true;
        api
            .get(`/suggestions/${field}`)
            .then((r) => mounted && setRemote(Array.isArray(r.data?.items) ? r.data.items : []))
            .catch((e) => logError("Autocomplete remote load failed:", e));
        return () => { mounted = false; };
    }, [field]);

    // De-dupe + rank options against the current query
    const options = useMemo(() => {
        const seen = new Set();
        const merged = [];
        [...(staticOptions || []), ...(remote || []), ...(extraOptions || [])].forEach((raw) => {
            const s = (raw || "").toString().trim();
            if (!s) return;
            const key = s.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            merged.push(s);
        });

        const q = (value || "").trim().toLowerCase();
        if (!q) return merged.slice(0, maxOptions);

        // Priority: starts-with > contains > everything else
        const startsWith = [];
        const contains = [];
        merged.forEach((opt) => {
            const lo = opt.toLowerCase();
            if (lo === q) return; // hide exact match — nothing to autocomplete
            if (lo.startsWith(q)) startsWith.push(opt);
            else if (lo.includes(q)) contains.push(opt);
        });
        return [...startsWith, ...contains].slice(0, maxOptions);
    }, [value, staticOptions, remote, extraOptions, maxOptions]);

    // Close on outside click
    useEffect(() => {
        const onDoc = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const commit = useCallback((val) => {
        onChange(val);
        setOpen(false);
        setHighlight(-1);
    }, [onChange]);

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setHighlight((h) => Math.min(options.length - 1, (h < 0 ? -1 : h) + 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(0, h - 1));
        } else if (e.key === "Enter") {
            if (open && highlight >= 0 && options[highlight]) {
                e.preventDefault();
                commit(options[highlight]);
            } else if (onEnter) {
                onEnter(e);
            }
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    // Scroll highlighted item into view
    useEffect(() => {
        if (!open || highlight < 0 || !listRef.current) return;
        const node = listRef.current.querySelector(`[data-idx="${highlight}"]`);
        if (node) node.scrollIntoView({ block: "nearest" });
    }, [open, highlight]);

    return (
        <div ref={rootRef} className="relative">
            <input
                data-testid={testId}
                type="text"
                value={value || ""}
                required={required}
                disabled={disabled}
                placeholder={placeholder}
                autoComplete="off"
                onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlight(-1); }}
                onFocus={() => setOpen(true)}
                onKeyDown={onKeyDown}
                className={className || "w-full bg-white border border-[#E5E5E5] px-4 py-2.5 text-base focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt disabled:opacity-60"}
            />
            {open && options.length > 0 && (
                <ul
                    ref={listRef}
                    data-testid={testId ? `${testId}-listbox` : undefined}
                    role="listbox"
                    className="absolute z-30 left-0 right-0 top-full mt-1 max-h-64 overflow-y-auto bg-white border border-[#E5E5E5] shadow-lg"
                >
                    {options.map((opt, idx) => (
                        <li
                            key={opt}
                            data-idx={idx}
                            data-testid={testId ? `${testId}-option-${idx}` : undefined}
                            onMouseDown={(e) => { e.preventDefault(); commit(opt); }}
                            onMouseEnter={() => setHighlight(idx)}
                            className={`px-4 py-2 text-sm cursor-pointer transition-colors ${
                                highlight === idx
                                    ? "bg-cobalt/10 text-ink"
                                    : "text-[#262626] hover:bg-[#FAFAFA]"
                            }`}
                        >
                            {opt}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
